import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, X, CopyPlus, Loader2, ChevronDown, Search } from 'lucide-react';
import { Button, Container, Input, Select, Stack, cn, FurnitureIcon, FURNITURE_GROUPS } from '@easymoving/ui';
import { useBackend } from '../../lib/backend/context';
import { useCustomRooms } from '../../hooks/useUserData';
import { searchFurnitureTypes } from '../../lib/furniture/search';
import { roomOptions } from '../../lib/taxonomy/options';
import { AddModeTabs, type AddMode } from '../../components/items/AddModeTabs';
import { PhotoCaptureMode } from '../../components/items/PhotoCaptureMode';

const NONE = '__none__';
const MAX_QTY = 99;
const LAST_GROUP_KEY = 'easymoving.lastAddGroup';
const LAST_MODE_KEY = 'easymoving.lastAddMode';

function readLastGroup(): string {
  try {
    return localStorage.getItem(LAST_GROUP_KEY) || FURNITURE_GROUPS[0].group;
  } catch {
    return FURNITURE_GROUPS[0].group;
  }
}

function readLastMode(): AddMode {
  try {
    return localStorage.getItem(LAST_MODE_KEY) === 'photos' ? 'photos' : 'icons';
  } catch {
    return 'icons';
  }
}

/** `?mode=` overrides the remembered mode for this visit only (e.g. the
 * retired /app/capture route redirects here wanting the photo tab, without
 * touching what a plain visit to /app/items/new remembers next time). */
function initialMode(param: string | null): AddMode {
  if (param === 'photos' || param === 'icons') return param;
  return readLastMode();
}

interface Line {
  id: number;
  type: string;
  nickname: string; // optional friendly label for the whole group (empty = none)
  qty: number; // how many separate items to create
}

/**
 * Icon-first add (smooth bulk add): tap furniture icons to drop them into a
 * basket, bump quantities with −/+, name variants of the same icon separately,
 * then create them all as individual items. Photos are added per item later in
 * the gallery. The detailed single-item form lives one tap away at /details.
 */
export function IconAddPage() {
  const { t } = useTranslation();
  const backend = useBackend();
  const navigate = useNavigate();
  const customRooms = useCustomRooms();
  const [searchParams] = useSearchParams();

  const [lines, setLines] = useState<Line[]>([]);
  const [room, setRoom] = useState(NONE); // applies to the whole batch
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const seq = useRef(0);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [openGroup, setOpenGroup] = useState<string>(readLastGroup);
  const [search, setSearch] = useState('');
  const matches = search.trim() ? searchFurnitureTypes(search) : null;
  const [mode, setMode] = useState<AddMode>(() => initialMode(searchParams.get('mode')));

  function changeMode(next: AddMode) {
    setMode(next);
    try {
      localStorage.setItem(LAST_MODE_KEY, next);
    } catch {
      /* private mode — fall back to the in-memory value */
    }
  }

  const scrollToPalette = () =>
    paletteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  function toggleGroup(group: string) {
    if (openGroup === group) {
      setOpenGroup(''); // collapse
      return;
    }
    setOpenGroup(group);
    try {
      localStorage.setItem(LAST_GROUP_KEY, group); // remember the last room worked on
    } catch {
      /* ignore */
    }
  }

  const label = (type: string) => t(`furniture.type.${type}`);

  // Tap a grid icon: bump the un-nicknamed line for this type, else start a new one.
  function addType(type: string) {
    setLines((prev) => {
      const existing = prev.find((l) => l.type === type && !l.nickname && l.qty < MAX_QTY);
      if (existing) {
        return prev.map((l) => (l === existing ? { ...l, qty: l.qty + 1 } : l));
      }
      return [...prev, { id: ++seq.current, type, nickname: '', qty: 1 }];
    });
  }

  // Palette − : drop one off the last line of this type (remove the line at 0).
  function decType(type: string) {
    setLines((prev) => {
      let idx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i]!.type === type) {
          idx = i;
          break;
        }
      }
      if (idx < 0) return prev;
      if (prev[idx]!.qty <= 1) return prev.filter((_, i) => i !== idx);
      return prev.map((l, i) => (i === idx ? { ...l, qty: l.qty - 1 } : l));
    });
  }

  function setQty(id: number, delta: number) {
    setLines((prev) =>
      prev.flatMap((l) => {
        if (l.id !== id) return [l];
        const qty = l.qty + delta;
        if (qty < 1) return []; // − at 1 removes the line
        return [{ ...l, qty: Math.min(qty, MAX_QTY) }];
      }),
    );
  }

  function rename(id: number, nickname: string) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, nickname } : l)));
  }

  function duplicate(type: string) {
    setLines((prev) => [...prev, { id: ++seq.current, type, nickname: '', qty: 1 }]);
  }

  function remove(id: number) {
    setLines((prev) => prev.filter((l) => l.id !== id));
  }

  const total = lines.reduce((n, l) => n + l.qty, 0);

  async function createAll() {
    if (!total || busy) return;
    setBusy(true);
    setProgress(0);
    try {
      for (const line of lines) {
        const nickname = line.nickname.trim() || undefined;
        for (let i = 0; i < line.qty; i++) {
          await backend.createItem({
            name: label(line.type),
            nickname,
            iconType: line.type,
            room: room === NONE ? undefined : room,
            destination: 'pack',
            progressStatus: 'not_started',
          });
          setProgress((p) => p + 1);
        }
      }
      navigate('/app');
    } finally {
      setBusy(false);
    }
  }

  const roomOpts = [{ value: NONE, label: '—' }, ...roomOptions(t, customRooms)];

  // One palette card — used both inside the groups and in the flat search results.
  function renderCard(k: string) {
    const n = lines.filter((l) => l.type === k).reduce((s, l) => s + l.qty, 0);
    const cardLabel = label(k);
    return (
      <div
        key={k}
        className={cn(
          'relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-colors',
          n > 0 ? 'border-petrol bg-petrol-tint' : 'border-rule',
        )}
      >
        <button
          type="button"
          aria-label={cardLabel}
          onClick={() => addType(k)}
          className="w-full flex flex-col items-center gap-1.5 hover:opacity-90 transition-opacity"
        >
          <span className="size-20 rounded-lg bg-petrol-tint flex items-center justify-center">
            <FurnitureIcon type={k} className="size-14" />
          </span>
          <span className="text-[13px] text-graphite leading-tight text-center">{cardLabel}</span>
        </button>
        {/* Stepper overlays the bottom so the card height never changes (no grid shift). */}
        {n > 0 && (
          <div
            className="absolute inset-x-2 bottom-2 h-8 rounded-full bg-surface shadow-card flex items-center justify-between px-1"
            data-testid={`palette-step-${k}`}
          >
            <PaletteStep icon={Minus} aria-label={`${t('iconAdd.less')} ${cardLabel}`} onClick={() => decType(k)} />
            <span className="tabular-nums text-[15px] font-semibold text-petrol-deep">{n}</span>
            <PaletteStep icon={Plus} aria-label={`${t('iconAdd.more')} ${cardLabel}`} onClick={() => addType(k)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <Container size="narrow" className="pt-6 pb-32">
      <AddModeTabs mode={mode} onChange={changeMode} />
      {mode === 'photos' ? (
        <PhotoCaptureMode />
      ) : (
        <>
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-display text-2xl tracking-snug">{t('iconAdd.title')}</h1>
          <Link to="/app/items/new/details" className="text-petrol text-[14px] font-medium">
            {t('iconAdd.detailed')}
          </Link>
        </div>
        <p className="text-graphite text-[14px] mb-4">{t('iconAdd.intro')}</p>

        <Stack gap="md">
          <label className="block">
            <span className="smallcaps text-graphite">{t('iconAdd.roomBatch')}</span>
            <Select ariaLabel={t('iconAdd.roomBatch')} value={room} options={roomOpts} onValueChange={setRoom} />
          </label>

          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 size-4 text-graphite-soft" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('iconAdd.searchPlaceholder')}
              aria-label={t('iconAdd.searchPlaceholder')}
              className="ps-9 pe-9"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label={t('iconAdd.clearSearch')}
                className="absolute top-1/2 -translate-y-1/2 end-2 size-7 rounded-md flex items-center justify-center text-graphite-soft hover:bg-surface-tint"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          {/* The basket — what will be created. */}
          {lines.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid="basket">
              {lines.map((l) => {
                const lineLabel = l.nickname.trim() || label(l.type);
                return (
                  <li key={l.id} className="flex items-center gap-2 surface px-2.5 py-2">
                    <span className="size-10 shrink-0 rounded-md bg-petrol-tint flex items-center justify-center">
                      <FurnitureIcon type={l.type} className="size-7" />
                    </span>
                    <Input
                      value={l.nickname}
                      aria-label={`${t('iconAdd.nickname')} — ${label(l.type)}`}
                      placeholder={label(l.type)}
                      onChange={(e) => rename(l.id, e.target.value)}
                      className="flex-1 min-w-0"
                    />
                    <div className="flex items-center gap-1 shrink-0">
                      <Stepper
                        aria-label={`${t('iconAdd.less')} ${lineLabel}`}
                        icon={Minus}
                        onClick={() => setQty(l.id, -1)}
                      />
                      <span className="w-7 text-center tabular-nums text-[15px]" data-testid="line-qty">
                        {l.qty}
                      </span>
                      <Stepper
                        aria-label={`${t('iconAdd.more')} ${lineLabel}`}
                        icon={Plus}
                        onClick={() => setQty(l.id, +1)}
                      />
                    </div>
                    <button
                      type="button"
                      aria-label={`${t('iconAdd.duplicate')} ${lineLabel}`}
                      onClick={() => duplicate(l.type)}
                      className="shrink-0 size-8 rounded-md text-graphite-soft hover:bg-surface-tint hover:text-graphite flex items-center justify-center"
                    >
                      <CopyPlus className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`${t('iconAdd.remove')} ${lineLabel}`}
                      onClick={() => remove(l.id)}
                      className="shrink-0 size-8 rounded-md text-graphite-soft hover:bg-surface-tint hover:text-clay flex items-center justify-center"
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Always nudge for one more once the basket has something. */}
          {lines.length > 0 && (
            <button
              type="button"
              onClick={scrollToPalette}
              className="flex items-center justify-center gap-2 w-full py-4 rounded-xl border border-dashed border-petrol/50 text-petrol hover:bg-petrol-tint transition-colors"
            >
              <span className="size-7 rounded-full bg-petrol text-white flex items-center justify-center">
                <Plus className="size-4" />
              </span>
              <span className="text-[15px] font-medium">{t('iconAdd.addMore')}</span>
            </button>
          )}

          {/* Search results — a flat grid across all groups/languages. */}
          {matches && (
            <div data-testid="icon-palette">
              {matches.length === 0 ? (
                <p className="text-graphite-soft text-[14px] py-6 text-center">
                  {t('iconAdd.noResults', { query: search.trim() })}
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2.5">{matches.map(renderCard)}</div>
              )}
            </div>
          )}

          {/* The icon palette — collapsible groups; the last one used stays open. */}
          {!matches && (
          <div ref={paletteRef} className="flex flex-col gap-2 scroll-mt-4" data-testid="icon-palette">
            {FURNITURE_GROUPS.map((g) => {
              const groupCount = lines
                .filter((l) => (g.keys as readonly string[]).includes(l.type))
                .reduce((s, l) => s + l.qty, 0);
              const open = openGroup === g.group;
              return (
                <div key={g.group} className="surface overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.group)}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-surface-tint transition-colors"
                  >
                    <ChevronDown className={cn('size-4 text-graphite-soft transition-transform', open && 'rotate-180')} />
                    <span className="font-medium text-[15px]">{t(`furniture.group.${g.group}`)}</span>
                    {groupCount > 0 && (
                      <span className="min-w-5 h-5 px-1.5 rounded-full bg-petrol text-white text-[12px] font-semibold flex items-center justify-center tabular-nums">
                        {groupCount}
                      </span>
                    )}
                  </button>
                  {open && <div className="grid grid-cols-3 gap-2.5 p-3 pt-0">{g.keys.map(renderCard)}</div>}
                </div>
              );
            })}
          </div>
          )}
        </Stack>

        {/* Sticky create bar. */}
        {total > 0 && (
          <div className="fixed inset-x-0 bottom-16 md:bottom-0 z-20 border-t hairline bg-bg/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
            <div className="mx-auto max-w-[640px] px-4 py-3">
              <Button size="lg" className="w-full" disabled={busy} onClick={createAll}>
                {busy ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    {t('iconAdd.creating', { done: progress, total })}
                  </>
                ) : (
                  t('iconAdd.create', { count: total })
                )}
              </Button>
            </div>
          </div>
        )}
        </>
      )}
    </Container>
  );
}

function Stepper({
  icon: Icon,
  onClick,
  'aria-label': ariaLabel,
}: {
  icon: typeof Minus;
  onClick: () => void;
  'aria-label': string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="size-8 rounded-md border border-rule-strong text-ink hover:border-ink flex items-center justify-center"
    >
      <Icon className="size-4" />
    </button>
  );
}

/** Compact ± button shown on a palette card once it has a count. */
function PaletteStep({
  icon: Icon,
  onClick,
  'aria-label': ariaLabel,
}: {
  icon: typeof Minus;
  onClick: () => void;
  'aria-label': string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="size-7 rounded-full bg-petrol text-white hover:bg-petrol-deep flex items-center justify-center"
    >
      <Icon className="size-4" />
    </button>
  );
}
